import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {ApiKeyService} from '../../../services/api-key.service';
import {ToastService} from '../../../services/toast.service';
import {ApiKeyModel, API_KEY_LENGTH, API_KEY_MAX_KEYS_PER_USER} from '../../../models/api-key.model';
import {ButtonComponent} from '../../shared/button/button.component';
import {IconComponent} from '../../shared/icon/icon.component';
import {EditableTextComponent} from '../../shared/editable-text/editable-text.component';
import {DialogComponent} from '../../shared/dialog/dialog.component';

@Component({
  selector: 'app-api-keys',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    EditableTextComponent,
    DialogComponent
  ],
  templateUrl: './api-keys.component.html',
  styleUrl: './api-keys.component.scss'
})
export class ApiKeysComponent implements OnInit {
  protected readonly apiKeyService = inject(ApiKeyService);
  private readonly toastService = inject(ToastService);

  protected readonly apiKeys = signal<ApiKeyModel[]>([]);
  protected readonly showCreateDialog = signal(false);
  protected readonly newKeyName = signal('');
  protected readonly newKeyExpiration = signal<number | null>(30);
  protected readonly isCreating = signal(false);
  protected readonly isLoading = signal(true);

  protected readonly API_KEY_LENGTH = API_KEY_LENGTH;
  protected readonly MAX_KEYS = API_KEY_MAX_KEYS_PER_USER;

  protected readonly nameControls = new Map<string, FormControl>();

  ngOnInit(): void {
    this.loadKeys();
  }

  private loadKeys(): void {
    this.isLoading.set(true);
    this.apiKeyService.list().subscribe({
      next: (response) => {
        this.apiKeys.set(response.items);
        this.isLoading.set(false);
        response.items.forEach(key => {
          this.nameControls.set(key.id, new FormControl(key.name || '', [
            Validators.maxLength(100)
          ]));
        });
      },
      error: (error) => {
        this.isLoading.set(false);
        this.toastService.error('Error', 'Failed to load API keys');
      }
    });
  }

  protected getNameControl(keyId: string): FormControl {
    if (!this.nameControls.has(keyId)) {
      this.nameControls.set(keyId, new FormControl('', [Validators.maxLength(100)]));
    }
    return this.nameControls.get(keyId)!;
  }

  protected openCreateDialog(): void {
    this.newKeyName.set('');
    this.newKeyExpiration.set(30);
    this.showCreateDialog.set(true);
  }

  protected closeCreateDialog(): void {
    this.showCreateDialog.set(false);
  }

  protected createKey(): void {
    this.isCreating.set(true);
    this.toastService.handleResult(
      'API Key',
      this.apiKeyService.create({
        name: this.newKeyName().trim() || undefined,
        expires_in_days: this.newKeyExpiration()
      }),
      (key) => {
        this.closeCreateDialog();
        this.isCreating.set(false);
        this.apiKeys.update(keys => [...keys, key]);
        this.nameControls.set(key.id, new FormControl(key.name || '', [
          Validators.maxLength(100)
        ]));
      },
      {successMessage: 'API key created successfully'}
    );
  }

  protected updateKeyName(key: ApiKeyModel): void {
    const control = this.getNameControl(key.id);
    if (!control.valid) return;

    const newName = control.value?.trim();
    this.toastService.handleResult(
      'API Key',
      this.apiKeyService.updateKey(key.id, {name: newName || undefined}),
      (updatedKey) => {
        this.apiKeys.update(keys => keys.map(k => k.id === key.id ? updatedKey : k));
      },
      {successMessage: 'API key name updated'}
    );
  }

  protected expireKey(key: ApiKeyModel): void {
    if (!confirm(`Are you sure you want to expire "${key.name || 'Unnamed key'}"?`)) {
      return;
    }

    this.toastService.handleResult(
      'API Key',
      this.apiKeyService.updateKey(key.id, {force_expire: true}),
      (updatedKey) => {
        this.apiKeys.update(keys => keys.map(k => k.id === key.id ? updatedKey : k));
      },
      {successMessage: 'API key expired'}
    );
  }

  protected deleteKey(key: ApiKeyModel): void {
    if (!confirm(`Are you sure you want to delete "${key.name || 'Unnamed key'}"?`)) {
      return;
    }

    this.toastService.handleResult(
      'API Key',
      this.apiKeyService.delete(key.id),
      () => {
        this.apiKeys.update(keys => keys.filter(k => k.id !== key.id));
        this.nameControls.delete(key.id);
      },
      {successMessage: 'API key deleted'}
    );
  }

  protected copyToClipboard(key: string): void {
    navigator.clipboard.writeText(key).then(() => {
      this.toastService.success('Copied', 'API key copied to clipboard');
    }).catch(() => {
      this.toastService.error('Error', 'Failed to copy to clipboard');
    });
  }

  protected formatExpiration(key: ApiKeyModel): string {
    if (key.expired) {
      return 'EXPIRED';
    }
    if (!key.expiresAt) {
      return 'Never';
    }

    let text = key.expiresAt.toLocaleDateString();
    if (key.expiringSoon && !key.expired) {
      text += ` (${key.daysUntilExpiration}} days remaining)`
    }

    return text
  }

  protected get canCreateKey(): boolean {
    return this.apiKeys().length < this.MAX_KEYS;
  }
}
